import { Component, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { DatePipe } from '@angular/common';
import { FormControl,ReactiveFormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ShellCoursesDto } from '../../dtos/shell-courses.dto';
import { FieldsetModule } from 'primeng/fieldset';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DialogService } from 'primeng/dynamicdialog';
import { ShellCoursesService } from '../../services/shell-courses.service';
import { GetShellCoursesResponseDto } from '../../dtos/get-shell-courses.response.dto';
import { LessonsToolsMappingService } from '../../../deeplink/services/lessons-tools-mapping.service';
import { ToastService } from '../../../../common/services/toast.service';

@Component({
  selector: 'app-view-shell-courses',
  standalone: true,
  imports: [
      TableModule,
      FieldsetModule,
      TooltipModule,
      ButtonModule,
      MessageModule,
      RouterModule,
      DatePipe,
      FloatLabelModule,
      ReactiveFormsModule,
      InputTextModule,
      IftaLabelModule,
  ],
  providers: [ShellCoursesService,DialogService,LessonsToolsMappingService,ToastService],
  templateUrl: './view-shell-courses.html',
  styleUrls: ['./view-shell-courses.css'],
})
export class ViewShellCoursesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private isSearchTriggered = false;

  loading = signal<boolean>(false);
  shellCourses:Array<ShellCoursesDto> = new Array<ShellCoursesDto>()
  shellCoursesService = inject(ShellCoursesService)
  dialogService = inject(DialogService);
  private router = inject(Router);
  selectedShellCourse:ShellCoursesDto | null = null;
  first: number = 0;
  rows: number = 10;
  totalRecords = signal<number>(0);
  sortField = 'code';
  sortOrder = 'ASC';
  searchQuery:FormControl = new FormControl();
  toastService = inject(ToastService);
  //confirmDialogRef: DynamicDialogRef | null;

  ngOnInit() {
    this.searchQuery.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value: string) => {
      if (value && value.length >= 3) {
        this.isSearchTriggered = true;
        this.first = 0;
        this.loadShellCourses({
          skip: 0,
          limit: this.rows,
          sortField: this.sortField,
          sortOrder: this.sortOrder
        });
      } else if (!value || value.length === 0) {
        // Reset to show all results when search is cleared
        this.isSearchTriggered = true;
        this.first = 0;
        this.loadShellCourses({
          skip: 0,
          limit: this.rows,
          sortField: this.sortField,
          sortOrder: this.sortOrder
        });
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    // Skip if this was triggered by search resetting first
    if (this.isSearchTriggered) {
      this.isSearchTriggered = false;
      return;
    }
    
    // Update stored values for use by search
    this.sortField = event.sortField as string ?? 'code';
    this.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
    this.rows = event.rows ?? 10;
    
    this.loadShellCourses({ 
      skip: event.first ?? 0,
      limit: this.rows,
      sortField: this.sortField,
      sortOrder: this.sortOrder 
    });
  }

  async loadShellCourses({skip,limit,sortField,sortOrder}:{skip:number,limit:number,sortField:string,sortOrder:string}){
    try{
      this.loading.set(true);
      const response = await this.shellCoursesService.getPaginatedShellCourses({skip,limit,sortField,sortOrder,searchQuery:this.searchQuery.value});
      this.shellCourses = response.data
      this.totalRecords.set(response.total);
    }catch(e){
       //TODO: show error toastie
       throw e;
    } finally {
      this.loading.set(false);
    }
  } 
  goToAddShellCourse(){
    this.router.navigate([`/add-shell-courses`])
  }
}
